import {
  AspectRatio,
  Box,
  Button,
  CloseButton,
  Flex,
  Group,
  Image,
  Paper,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconFile } from "@tabler/icons-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Carousel } from "@mantine/carousel";

import { isFileFormatValid } from "../utils/files";

interface Props {
  onChange: (files: File[]) => void;
  files: File[];
  acceptedTypes?: string[];
  fileIcon?: ReactNode;
  title?: string;
  description?: string;
  imageMode?: boolean;
  uploadText?: string;
}

export const DroppableFileInput = ({
  acceptedTypes,
  onChange,
  files,
  fileIcon = <IconFile />,
  title,
  description,
  imageMode,
  uploadText,
}: Props) => {
  const { t } = useTranslation("forms");

  const [isFileDragged, setIsFileDragged] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const inputFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputFileRef.current) {
      const dataTransfer = new DataTransfer();
      files.forEach(file => dataTransfer.items.add(file));
      inputFileRef.current.files = dataTransfer.files;
    }
  }, [files]);

  const isFileAlreadyUploaded = (file: File) =>
    files.some(({ name }) => name === file.name);

  const addFiles = (filesToAdd: FileList) => {
    const newErrors: string[] = [];

    Array.from(filesToAdd).forEach(file => {
      if (!isFileFormatValid({ file, acceptedTypes })) {
        newErrors.push(
          t(`components.droppableFileInput.errors.invalidType`, {
            filename: file.name,
          })
        );
      }

      if (isFileAlreadyUploaded(file)) {
        newErrors.push(
          t(`components.droppableFileInput.errors.alreadyExists`, {
            filename: file.name,
          })
        );
      }
    });

    if (newErrors.length) {
      setErrors(newErrors);
      return;
    }

    setErrors([]);
    onChange([...files, ...Array.from(filesToAdd)]);
  };

  return (
    <>
      <Paper
        w="100%"
        radius="md"
        bd={isFileDragged ? "2px solid blue" : "2px solid rgb(0,0,0, 0)"}
        p="2"
        onDragOver={e => {
          e.preventDefault();

          setErrors([]);
          setIsFileDragged(true);
        }}
        onDragLeave={e => {
          e.preventDefault();
          setIsFileDragged(false);
        }}
        onDrop={e => {
          e.preventDefault();
          setIsFileDragged(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <Paper w="100%" radius="md" bd="2px dashed rgb(128,128,128, 0.3)">
          <Stack w="100%" h="100%" align="center" justify="center" p="xl">
            <ThemeIcon variant="light" size="xl">
              {fileIcon}
            </ThemeIcon>
            {title && <Title order={4}>{title}</Title>}
            {description && (
              <Text ta="center" c="dimmed">
                {description}
              </Text>
            )}
            <Button
              variant="light"
              onClick={() => {
                setErrors([]);
                inputFileRef.current?.click();
              }}
            >
              {uploadText ||
                t("components.droppableFileInput.labels.selectFiles")}
            </Button>
            <input
              accept={acceptedTypes?.join(",")}
              aria-label={t(`components.droppableFileInput.labels.input`)}
              type="file"
              multiple
              ref={inputFileRef}
              style={{ display: "none" }}
              onChange={e => {
                if (e.target.files) {
                  addFiles(e.target.files);
                }
              }}
            />

            {!imageMode && (
              <Stack>
                {files.map(currentFile => (
                  <Group key={currentFile.name} gap="0">
                    <Text c="blue" size="xs">
                      {currentFile.name}
                    </Text>
                    <CloseButton
                      c="blue"
                      size="xs"
                      onClick={() => {
                        setErrors([]);
                        onChange(
                          files.filter(file => file.name !== currentFile.name)
                        );
                      }}
                    />
                  </Group>
                ))}
              </Stack>
            )}
            <Stack gap="0" align="center">
              {errors.map((error, i) => (
                <Text key={i} c="red" fw={500}>
                  {error}
                </Text>
              ))}
            </Stack>
          </Stack>
        </Paper>
      </Paper>
      {imageMode && files.length > 0 && (
        <Carousel
          withIndicators
          withControls
          slideSize="33.3333%"
          emblaOptions={{
            align: "start",
          }}
          slideGap="md"
          draggable
        >
          {files.map(file => (
            <Carousel.Slide key={file.name}>
              <Flex justify="center" align="center" h="100%">
                <Box pos="relative" w={100}>
                  <CloseButton
                    size="sm"
                    pos="absolute"
                    top={4}
                    right={4}
                    bg="rgba(255, 255, 255, 0.78)"
                    c="dark"
                    radius="xl"
                    style={{
                      zIndex: 1,
                      backdropFilter: "blur(4px)",
                      border: "1px solid rgba(255, 255, 255, 0.45)",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
                    }}
                    onClick={() => {
                      setErrors([]);
                      onChange(
                        files.filter(
                          currentFile => currentFile.name !== file.name
                        )
                      );
                    }}
                  />
                  <AspectRatio ratio={1} w={100}>
                    <Image
                      src={URL.createObjectURL(file)}
                      alt={file.name}
                      fit="cover"
                      h="100%"
                      w="100%"
                      radius="md"
                    />
                  </AspectRatio>
                </Box>
              </Flex>
            </Carousel.Slide>
          ))}
        </Carousel>
      )}
    </>
  );
};
